type FuncionarioComId = {
  id?: string | number
  [chave: string]: unknown
}

export type FuncionariosPorStatus = {
  emAtraso: FuncionarioComId[]
  aIniciar: FuncionarioComId[]
  trabalhando: FuncionarioComId[]
  ausentes: FuncionarioComId[]
}

const chaveDoFuncionario = (funcionario: FuncionarioComId, indice: number) => {
  return funcionario?.id == null ? `sem-id-${indice}` : String(funcionario.id)
}

const deduplicar = (funcionarios: FuncionarioComId[]) => {
  const vistos = new Set<string>()

  return funcionarios.filter((funcionario, indice) => {
    const chave = chaveDoFuncionario(funcionario, indice)
    if (vistos.has(chave)) return false
    vistos.add(chave)
    return true
  })
}

/**
 * Normaliza os quatro estados exibidos na Home.
 *
 * O endpoint pode devolver o mesmo empregado em um estado operacional e em um
 * evento de ausência. Estados operacionais têm precedência sobre "ausentes",
 * evitando que alguém "a iniciar" seja apresentado também como ausente.
 */
export const normalizarFuncionariosPorStatus = (status: FuncionariosPorStatus): FuncionariosPorStatus => {
  const emAtraso = deduplicar(status.emAtraso)
  const aIniciar = deduplicar(status.aIniciar)
  const trabalhando = deduplicar(status.trabalhando)

  const idsOperacionais = new Set(
    [...emAtraso, ...aIniciar, ...trabalhando]
      .map(funcionario => funcionario.id)
      .filter(id => id != null)
      .map(String)
  )

  const ausentes = deduplicar(status.ausentes)
    .filter(funcionario => funcionario.id == null || !idsOperacionais.has(String(funcionario.id)))

  return { emAtraso, aIniciar, trabalhando, ausentes }
}

type EstatisticaTravamento = {
  notStarted?: number
  empPending?: number
  managerPending?: number
  finished?: number
}

export type EstadoTravamento = {
  chave: 'faltamTravar' | 'iniciadas' | 'travadasAssinadas'
  tom: 'alerta' | 'acento' | 'sucesso'
  valor: number
}

/**
 * Converte os buckets reais do locker-statistics nos estados exibidos.
 * empPending e managerPending significam que uma das partes já assinou; a API
 * não fornece um bucket confiável de "folhas com pendências".
 */
export const montarEstadosTravamento = (dados: EstatisticaTravamento): EstadoTravamento[] => [
  { chave: 'faltamTravar', tom: 'alerta', valor: dados.notStarted || 0 },
  {
    chave: 'iniciadas',
    tom: 'acento',
    valor: (dados.empPending || 0) + (dados.managerPending || 0)
  },
  { chave: 'travadasAssinadas', tom: 'sucesso', valor: dados.finished || 0 }
]
